import React, { useState } from 'react';
import { Button, Input, Toast, Modal } from '../components/common';
import { useAuth } from '../context/AuthContext';
import statementService from '../services/statement.service';
import './AccountStatement.css';

type PeriodoTipo = 'mes_actual' | 'mes_anterior';

const calcularFechas = (tipo: PeriodoTipo) => {
  const now = new Date();
  let startDate = '';
  let endDate = '';

  switch (tipo) {
    case 'mes_actual':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case 'mes_anterior':
      const primerDiaMesAnterior = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const ultimoDiaMesAnterior = new Date(now.getFullYear(), now.getMonth(), 0);
      startDate = primerDiaMesAnterior.toISOString().split('T')[0];
      endDate = ultimoDiaMesAnterior.toISOString().split('T')[0];
      break;
  }

  return { startDate, endDate };
};

const AccountStatement: React.FC = () => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [periodoTipo, setPeriodoTipo] = useState<PeriodoTipo>('mes_actual');
  const [dateRange, setDateRange] = useState(() => calcularFechas('mes_actual'));
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      await statementService.generateAndDownloadPDF(
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      setToast({
        isVisible: true,
        message: 'Estado de cuenta generado exitosamente',
        type: 'success'
      });
    } catch (error) {
      setToast({
        isVisible: true,
        message: 'Error al generar el estado de cuenta',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenEmailModal = () => {
    setEmailAddress(user?.email || '');
    setIsEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      setToast({
        isVisible: true,
        message: 'Por favor ingresa un correo electrónico',
        type: 'error'
      });
      return;
    }

    try {
      setIsSendingEmail(true);
      const result = await statementService.sendByEmail({
        email: emailAddress,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined
      });

      setIsEmailModalOpen(false);
      setToast({
        isVisible: true,
        message: result.message || `Estado de cuenta enviado a ${result.sentTo}`,
        type: 'success'
      });
    } catch (error: any) {
      setToast({
        isVisible: true,
        message: error.response?.data?.message || 'Error al enviar el correo electrónico',
        type: 'error'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handlePeriodoChange = (tipo: PeriodoTipo) => {
    setPeriodoTipo(tipo);
    setDateRange(calcularFechas(tipo));
  };

  return (
    <div className="account-statement">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      {/* Header Card */}
      <div className="statement-header-card">
        <div className="header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </div>
        <div className="header-content">
          <h2>Generar Estado de Cuenta</h2>
          <p>Descarga o envía por correo tu estado de cuenta en formato PDF con el detalle de todas tus facturas y pagos.</p>
        </div>
      </div>

      {/* Main Card */}
      <div className="statement-main-card">
        <div className="card-section">
          <h3>Datos del Cliente</h3>
          <div className="client-info">
            <div className="info-row">
              <span className="info-label">Nombre:</span>
              <span className="info-value">{user?.name || 'Usuario'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="card-divider"></div>

        <div className="card-section">
          <h3>Periodo del Reporte</h3>
          <div className="period-selector">
            <div className="period-buttons">
              <button
                className={`period-btn ${periodoTipo === 'mes_actual' ? 'active' : ''}`}
                onClick={() => handlePeriodoChange('mes_actual')}
              >
                Mes Actual
              </button>
              <button
                className={`period-btn ${periodoTipo === 'mes_anterior' ? 'active' : ''}`}
                onClick={() => handlePeriodoChange('mes_anterior')}
              >
                Mes Anterior
              </button>
            </div>
            <span className="period-label">
              {new Date(dateRange.startDate + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' — '}
              {new Date(dateRange.endDate + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>

        <div className="card-divider"></div>

        <div className="card-section">
          <h3>Contenido del Reporte</h3>
          <ul className="content-list">
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Resumen de saldos
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Detalle de facturas
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Historial de pagos
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Facturas pendientes de pago
            </li>
          </ul>
        </div>

        <div className="card-actions">
          <Button
            variant="primary"
            size="large"
            isLoading={isGenerating}
            onClick={handleGenerate}
            leftIcon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            }
          >
            {isGenerating ? 'Generando...' : 'Descargar PDF'}
          </Button>

          <Button
            variant="secondary"
            size="large"
            onClick={handleOpenEmailModal}
            leftIcon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            }
          >
            Enviar por Email
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="statement-info-card">
        <div className="info-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <div className="info-content">
          <h4>Información importante</h4>
          <p>
            El estado de cuenta refleja los movimientos registrados en el sistema hasta la fecha actual.
            Para aclaraciones sobre montos o transacciones, contacte a nuestro departamento de cuentas por cobrar.
          </p>
        </div>
      </div>

      {/* Email Modal */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title="Enviar Estado de Cuenta por Email"
        size="small"
      >
        <div className="email-modal-content">
          <p className="email-modal-description">
            Ingresa el correo electrónico donde deseas recibir tu estado de cuenta en formato PDF.
          </p>

          <Input
            label="Correo electrónico"
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder="correo@ejemplo.com"
            leftIcon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            }
          />

          {dateRange.startDate && dateRange.endDate && (
            <div className="email-period-info">
              <span>Periodo seleccionado:</span>
              <strong>{dateRange.startDate} - {dateRange.endDate}</strong>
            </div>
          )}

          <div className="email-modal-actions">
            <Button
              variant="ghost"
              onClick={() => setIsEmailModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              isLoading={isSendingEmail}
              onClick={handleSendEmail}
              leftIcon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              }
            >
              {isSendingEmail ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AccountStatement;
